import axios from 'axios';

// Dynamically determine the backend URL based on current domain
// This allows the app to work on both preview.emergentagent.com and custom domains
const getBackendUrl = () => {
  // In development or if explicitly set, use the env variable
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // In production, use the current origin (same domain for API)
  // This ensures cookies work properly on custom domains
  return window.location.origin;
};

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Export for use in other files
export const getApiUrl = () => API;
export const getBaseUrl = () => BACKEND_URL;

// ============= USER MANAGEMENT =============

export const createUser = async (userData) => {
  const response = await axios.post(`${API}/users`, userData);
  return response.data;
};

export const getUser = async (userId) => {
  const response = await axios.get(`${API}/users/${userId}`);
  return response.data;
};

export const getPractitionerClients = async (practitionerId) => {
  const response = await axios.get(`${API}/practitioners/${practitionerId}/clients`);
  return response.data;
};

export const getAllUsers = async (role = null) => {
  const url = role ? `${API}/users?role=${role}` : `${API}/users`;
  const response = await axios.get(url);
  return response.data;
};

// ============= ASSESSMENT MANAGEMENT =============

export const createAssessment = async (assessmentData) => {
  const response = await axios.post(`${API}/assessments`, assessmentData);
  return response.data;
};

export const getClientAssessments = async (clientId) => {
  const response = await axios.get(`${API}/clients/${clientId}/assessments`);
  return response.data;
};

// ============= PRESCRIPTION MANAGEMENT =============

export const generatePrescription = async (assessmentId, clientId) => {
  const response = await axios.post(`${API}/prescriptions/generate`, null, {
    params: { assessment_id: assessmentId, client_id: clientId }
  });
  return response.data;
};

export const getClientPrescriptions = async (clientId) => {
  const response = await axios.get(`${API}/clients/${clientId}/prescriptions`);
  return response.data;
};

export const updatePrescriptionSessions = async (prescriptionId, sessionIds) => {
  const response = await axios.put(`${API}/prescriptions/${prescriptionId}/sessions`, sessionIds);
  return response.data;
};

// ============= SESSION MANAGEMENT =============

export const getAllSessions = async (toolType = null) => {
  const url = toolType ? `${API}/sessions?tool_type=${toolType}` : `${API}/sessions`;
  const response = await axios.get(url);
  return response.data;
};

export const getSession = async (sessionId) => {
  const response = await axios.get(`${API}/sessions/${sessionId}`);
  return response.data;
};

// ============= PROGRESS TRACKING =============

export const createProgressRecord = async (progressData) => {
  const response = await axios.post(`${API}/progress`, progressData);
  return response.data;
};

export const getClientProgress = async (clientId, limit = 100) => {
  const response = await axios.get(`${API}/clients/${clientId}/progress?limit=${limit}`);
  return response.data;
};

export const getClientProgressStats = async (clientId) => {
  const response = await axios.get(`${API}/clients/${clientId}/progress/stats`);
  return response.data;
};

// ============= VOICE RECORDINGS =============

export const uploadVoiceRecording = async (clientId, sessionId, audioBlob) => {
  const formData = new FormData();
  formData.append('audio_file', audioBlob, 'recording.webm');
  formData.append('client_id', clientId);
  if (sessionId) formData.append('session_id', sessionId);
  
  const response = await axios.post(`${API}/voice-recordings/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const getClientVoiceRecordings = async (clientId) => {
  const response = await axios.get(`${API}/clients/${clientId}/voice-recordings`);
  return response.data;
};

// ============= EQUIPMENT STORE =============

export const getAllProducts = async (toolType = null) => {
  const url = toolType ? `${API}/products?tool_type=${toolType}` : `${API}/products`;
  const response = await axios.get(url);
  return response.data;
};

export const getProduct = async (productId) => {
  const response = await axios.get(`${API}/products/${productId}`);
  return response.data;
};

// ============= ORDER MANAGEMENT =============

export const createOrder = async (orderData) => {
  const response = await axios.post(`${API}/orders`, orderData);
  return response.data;
};

export const getUserOrders = async (userId) => {
  const response = await axios.get(`${API}/users/${userId}/orders`);
  return response.data;
};

// ============= HEALTH CHECK =============

export const healthCheck = async () => {
  const response = await axios.get(`${API}/health`);
  return response.data;
};
